/**
=========================================================
* Vue Soft UI Dashboard - v3.0.0
=========================================================

* Product Page: https://creative-tim.com/product/vue-soft-ui-dashboard-laravel
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { createApp } from "vue";
import App from "./App.vue";
import store from "./store";
import router from "./router";
import i18n from "./i18n";
import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";
import "./assets/css/global.css";
import "flag-icons/css/flag-icons.min.css";
import SoftUIDashboard from "./soft-ui-dashboard";
import axios from "axios";
import api from "@/services/apiClient";

import PrimeVue from "primevue/config";
import { definePreset } from "@primevue/themes";
import Aura from "@primevue/themes/aura";
import "primeicons/primeicons.css";
import { showToast } from "@/services/toastBus.js";

import QBtn     from "@/components/q/QBtn.vue";
import QBadge   from "@/components/q/QBadge.vue";
import QMiniCard from "@/components/q/QMiniCard.vue";
import QToggle  from "@/components/q/QToggle.vue";
import ToggleSwitch from "primevue/toggleswitch";

import VueTelInput from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";

// Interceptor global de errores HTTP — aplicado a ambas instancias de axios
const responseOk  = (response) => response;
const responseErr = (error) => {
  const status     = error?.response?.status;
  const requestUrl = error?.config?.url ?? "";

  if (status === 401) {
    if (!requestUrl.includes("/login")) {
      store.dispatch("auth/logout").finally(() => {
        router.push("/login");
      });
    }
    return Promise.reject(error);
  }

  if (status === 403) {
    if (!error.config?._silent403) {
      showToast('warn', "No tienes permisos para realizar esta acción.", 4000);
    }
    return Promise.reject(error);
  }

  if (status === 404 || status === 410) {
    return Promise.reject(error);
  }

  if (status >= 500) {
    showToast('error', "Error del servidor. Inténtalo de nuevo.", 5000);
    return Promise.reject(error);
  }

  if (!error.response) {
    showToast('error', "Error de conexión. Comprueba tu red.", 5000);
    return Promise.reject(error);
  }

  return Promise.reject(error);
};

api.interceptors.response.use(responseOk, responseErr);
axios.interceptors.response.use(responseOk, responseErr);

const TerminalNocturna = definePreset(Aura, {
  primitive: { borderRadius: { sm: '5px', md: '7px', lg: '8px', xl: '10px' } },
  semantic: {
    primary: {
      50:  '#fdf3df', 100: '#fbe4b3', 200: '#f8d183', 300: '#f7bd53', 400: '#f6ae35',
      500: '#f5a623', 600: '#d98a12', 700: '#a8620a', 800: '#7a4708', 900: '#4d2c05', 950: '#241a09',
    },
    colorScheme: {
      dark: {
        surface: {
          0:   '#0e141f', 50:  '#0b111a', 100: '#111926', 200: '#1b2532', 300: '#243040',
          400: '#3a4a60', 500: '#7c8ba1', 600: '#9aa8bd', 700: '#bcc7d7', 800: '#d7dfea',
          900: '#e7edf5', 950: '#f4f7fc',
        },
        primary: { color: '#f5a623', contrastColor: '#140c00', hoverColor: '#f6ae35', activeColor: '#f7bd53' },
        content:  { background: '#0e141f', borderColor: '#1b2532', hoverBackground: '#111926' },
        text:     { color: '#e7edf5', mutedColor: '#7c8ba1' },
      },
    },
  },
});

const appInstance = createApp(App);

appInstance.use(store);
appInstance.use(router);
appInstance.use(i18n);
appInstance.use(SoftUIDashboard);
appInstance.use(VueTelInput);
appInstance.component("QBtn",        QBtn);
appInstance.component("QBadge",      QBadge);
appInstance.component("QMiniCard",   QMiniCard);
appInstance.component("QToggle",     QToggle);
appInstance.component("ToggleSwitch", ToggleSwitch);

appInstance.use(PrimeVue, {
  theme: {
    preset: TerminalNocturna,
    options: {
      darkModeSelector: '.p-dark',
    },
  },
});

// Si hay token, al arrancar cargamos /me y permisos (evita menú vacío tras F5)
if (store.state.auth?.loggedIn) {
  store.dispatch("auth/cargarMeYPermisos").catch(() => {});
}

document.documentElement.classList.add('p-dark');
appInstance.mount("#app");